import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { sendMessageToAgent } from "../../store/userSlice";
import { v4 as uuid } from "uuid";
import { Agents } from "../../types/master";
import { MessageInputHandle } from "../ChatWindow/MessageInput";

/**
 * ChatCore - Hook for unified chat logic
 * Returns all the logic and state needed for chat components
 * Each component can render its own UI with its own styles
 */
export const useChatCore = (
  sessionId: string,
  currentAgent?: Agents,
  agentId?: string
) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<MessageInputHandle | null>(null);

  // Redux state
  const { selectedAgent, currentChat, loading, isViewingHistory } = useSelector(
    (state: RootState) => state.user
  );
  const { loading: agentsLoading } = useSelector(
    (state: RootState) => state.agents
  );

  // Local state
  const [value, setValue] = useState("");
  const [userMessage, setUserMessage] = useState<string>("");

  // Derived state
  const agent = currentAgent || selectedAgent;
  const hasMessages = currentChat && currentChat.length > 0;
  const showFullScreenSpinner = (loading && !hasMessages) || agentsLoading;

  // Get default prompts
  const defaultPrompts = useMemo(() => {
    if (!agent?.default_prompts) return [];
    try {
      return typeof agent.default_prompts === "string"
        ? JSON.parse(agent.default_prompts)
        : agent.default_prompts;
    } catch {
      return [];
    }
  }, [agent]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const message = userMessage || value;
    if (!message.trim()) return;

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    try {
      dispatch(
        sendMessageToAgent({
          agentId: agent!.id,
          message,
          questionId,
          timestamp,
          agentEndpoint: agent?.endpoint,
          sessionId,
        })
      );
    } catch (error) {
      console.error("Error while sending agent query", error);
    } finally {
      setValue("");
      setUserMessage("");
    }
  }, [dispatch, agent, sessionId, userMessage, value]);

  // Handle prompt click
  const handlePromptClick = useCallback((prompt: string) => {
    inputRef.current?.sendPrompt(prompt);
  }, []);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !loading) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [loading, handleSubmit]
  );

  return {
    // State
    agent,
    currentChat,
    loading,
    agentsLoading,
    hasMessages,
    showFullScreenSpinner,
    defaultPrompts,
    value,
    setValue,
    userMessage,
    setUserMessage,
    isViewingHistory,

    // Refs
    inputRef,

    // Handlers
    handleSubmit,
    handlePromptClick,
    handleKeyDown,
  };
};
